import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { api, Icon } from "../lib/core";
import { toast } from "sonner";

// ============================================================
// Discover / Offers hub
// ============================================================
export const DiscoverPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/offers").then((r) => setOffers(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(offers.map((o) => o.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [offers]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return offers.filter((o) => {
      if (cat !== "All" && o.category !== cat) return false;
      if (!term) return true;
      return [o.brand, o.title, o.subtitle, o.category].some((f) => (f || "").toLowerCase().includes(term));
    });
  }, [offers, cat, q]);

  const claim = (o) => {
    if (o.link) { window.open(o.link, "_blank", "noopener"); return; }
    if (navigator.clipboard) navigator.clipboard.writeText(`TOPIT-${(o.brand || "DEAL").toUpperCase().slice(0, 6)}`).catch(() => {});
    toast.success(`${o.cta || "Offer claimed"} — code copied!`);
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="discover-view">
      {/* hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#E11D2E] to-[#9d0f1c] p-6 text-white">
        <div className="absolute -right-6 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center"><Icon name="local_activity" className="text-white text-2xl" /></span>
          <div>
            <p className="font-extrabold text-xl leading-none">Discover deals & perks</p>
            <p className="text-white/80 text-sm mt-1.5">Exclusive brand offers for TopIt customers</p>
          </div>
        </div>
      </motion.div>

      {/* search + categories */}
      <div className="space-y-3">
        <div className="relative">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="discover-search" placeholder="Search offers, brands, categories…"
            className="w-full bg-white border border-slate-200 rounded-2xl pl-10 pr-4 py-3 text-sm outline-none focus:border-red-500" />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`discover-cat-${c}`}
              className={`shrink-0 text-xs font-bold px-4 py-2 rounded-full border transition-colors ${cat === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{c}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading offers…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/70" data-testid="discover-empty">
          <Icon name="local_offer" className="text-5xl text-slate-300" />
          <p className="mt-3 text-slate-600 font-semibold">No offers found</p>
          <p className="text-sm text-slate-400 mt-1">Try a different category or search term.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((o, i) => (
            <motion.div key={o.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="group bg-white rounded-3xl border border-slate-200/70 overflow-hidden flex flex-col" data-testid={`discover-offer-${i}`}>
              <div className="relative h-32 overflow-hidden" style={{ background: (o.accent || "#E11D2E") + "18" }}>
                {o.image ? (
                  <img src={o.image} alt={o.brand} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl">{o.emoji || "🎁"}</div>
                )}
                <span className="absolute top-3 left-3 text-[10px] font-bold text-white px-2.5 py-1 rounded-full backdrop-blur" style={{ background: (o.accent || "#E11D2E") + "cc" }}>{o.category || "Offer"}</span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-1.5">
                  {o.emoji && <span className="text-base">{o.emoji}</span>}
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: o.accent || "#E11D2E" }}>{o.brand}</p>
                </div>
                <p className="font-extrabold text-slate-900 mt-1 leading-tight">{o.title}</p>
                <p className="text-sm text-slate-500 mt-0.5 flex-1">{o.subtitle}</p>
                <button onClick={() => claim(o)} data-testid={`discover-claim-${i}`}
                  className="mt-3 w-full text-sm font-bold text-white py-2.5 rounded-xl transition-transform active:scale-95" style={{ background: o.accent || "#E11D2E" }}>
                  {o.cta || "Claim offer"}</button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
